import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, FileText, User } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
function BlogPostsGrid() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[420px] rounded-xl bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Não foi possível carregar os artigos.</p>
        <Button onClick={refetch} variant="outline">Tentar novamente</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 bg-muted/10 rounded-xl border border-dashed border-border">
        <FileText className="mx-auto h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-muted-foreground">Nenhum artigo técnico encontrado no momento.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group flex flex-col h-full overflow-hidden border-border/40 bg-card shadow-2xs transition-all duration-300 hover:border-primary/30 rounded-lg">
          <div className="relative h-52 w-full overflow-hidden bg-secondary-100">
            {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-secondary-200/30 text-primary-600">
                <FileText className="h-10 w-10 opacity-30" />
              </div>}
            <div className="absolute top-4 left-4">
              <Badge variant="default" className="bg-primary text-primary-foreground font-semibold px-3 py-1 rounded-sm">
                Técnico
              </Badge>
            </div>
          </div>

          <CardHeader className="pb-2 pt-6">
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
              <span className="flex items-center gap-1">
                <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
                {post.date ? new Date(post.date).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            }) : 'Data recente'}
              </span>
              {post.author && <>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
                  <span className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    {post.author.name}
                  </span>
                </>}
            </div>
            <CardTitle className="text-lg font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors text-foreground">
              <Link to={post.link || "#"}>
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-grow pb-4">
            <div className="text-secondary-700 text-sm line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
          __html: post.excerpt || ""
        }} />
          </CardContent>

          <CardFooter className="pt-2 pb-8">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between group/btn border-primary/20 text-primary hover:bg-primary hover:text-white transition-all rounded-sm font-semibold">
                Ler artigo completo
                <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function TechnicalContentAndArticles() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="conteúdo-técnico-e-artigos" className="relative py-24 bg-white overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" aria-hidden="true">
        <svg className="h-full w-full" width="100%" height="100%">
          <defs>
            <pattern id="grid-pattern-blog" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern-blog)" />
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-16 border-b border-secondary-200 pb-10">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-4 border-primary text-primary bg-white font-bold uppercase tracking-wider text-[10px] px-2 py-0.5 rounded-none">
              Blog & Artigos
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
              Conteúdo Técnico Especializado
            </h2>
            <p className="text-lg text-secondary-700 leading-relaxed max-w-2xl">
              Acompanhe nossas publicações sobre engenharia térmica, inovações em refrigeração crítica e melhores práticas para ambientes controlados.
            </p>
          </div>
          <div className="hidden md:block pb-1">
            <Link to="/blog">
              <Button variant="outline" className="gap-2 border-primary text-primary hover:bg-primary hover:text-white font-semibold rounded-sm h-11 px-8">
                Ver todos os artigos
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <BlogPostsGrid />
        </PostsProvider>

        <div className="mt-10 text-center md:hidden">
          <Link to="/blog">
            <Button variant="outline" className="w-full gap-2">
              Ver todos os artigos
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}